import { Table, Thead, Tbody, Tr, Th, Td, Box, Text, Collapse, HStack } from '@chakra-ui/react';
import { Fragment, useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import formatNumber from 'app/utils/formatting';
import { formatCurrency } from 'app/utils/strings';

import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

import {
  ActionIconButton,
  ArrowDownBold,
  PointsTransaction,
  SortArrowsAscBold,
  SortArrowsDescBold,
} from '@aslanpay/portal-common-lib';
import getPointsValue from 'app/utils/points';

type Props = {
  data: PointsTransaction[];
  index: number;
};

const PAGE_SIZE = 15;

function TopUpHistoryTable({ data, index }: Props) {
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);

  const [order, setOrder] = useState<string>('DESC');

  useEffect(() => {
    if (index === 1) {
      setPage(1);
    }
  }, [index]);

  const toggleRow = (id: string) => {
    setExpandedRowId((prev) => (prev === id ? null : id));
  };

  const sortClicked = () => {
    if (order === 'ASC') {
      setOrder('DESC');
    }

    if (order === 'DESC') {
      setOrder('ASC');
    }
  };

  const getOrderedData = () => {
    if (order === 'ASC') {
      const ordered = data.sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      return ordered;
    }

    const ordered = data.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    return ordered;
  };

  return (
    <Box overflowX="auto" w="full" maxH="600px">
      <Table variant="simple" size="sm">
        <Thead>
          <Tr>
            <Th onClick={sortClicked}>
              Date{' '}
              {order === 'DESC' ? (
                <SortArrowsDescBold color="brand.purple" boxSize="15px" />
              ) : (
                <SortArrowsAscBold color="brand.purple" boxSize="15px" />
              )}
            </Th>
            <Th>No. of points purchased</Th>
            <Th>Cost</Th>
            <Th />
          </Tr>
        </Thead>
        <Tbody>
          {getOrderedData()
            .slice(0, PAGE_SIZE * page)
            .map((tx) => {
              const isExpanded = expandedRowId === tx.id;
              return (
                <Fragment key={tx.id}>
                  <Tr
                    onClick={() => toggleRow(tx.id)}
                    _hover={{ bg: 'gray.50', cursor: 'pointer' }}
                  >
                    <Td>{format(parseISO(tx.createdAt), 'dd/MM/yyyy HH:mm')}</Td>
                    <Td>{formatNumber(tx.amount)}</Td>
                    <Td>{formatCurrency(getPointsValue(tx.amount), 'GBP')}</Td>
                    <Td textAlign="right" onClick={(e) => e.stopPropagation()}>
                      <ActionIconButton
                        aria-label={isExpanded ? 'Hide notes' : 'Show notes'}
                        icon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        onClick={() => toggleRow(tx.id)}
                      />
                    </Td>
                  </Tr>
                  {isExpanded && (
                    <Tr>
                      <Td colSpan={4} p={0} borderBottomWidth="0">
                        <Collapse in={isExpanded} animateOpacity unmountOnExit>
                          <Box bg="gray.50" p={3} fontSize="sm" color="gray.700">
                            <strong>Notes:</strong> {tx.notes || 'No notes provided.'}
                          </Box>
                        </Collapse>
                      </Td>
                    </Tr>
                  )}
                </Fragment>
              );
            })}
        </Tbody>
      </Table>
      {data.length > PAGE_SIZE * page && (
        <Box
          w="full"
          borderRadius="8px"
          border="1px solid"
          borderColor="brand.purple"
          px="20px"
          py="10px"
          mt="32px"
          onClick={() => setPage(page + 1)}
          _hover={{
            cursor: 'pointer',
          }}
        >
          <HStack justifyContent="center">
            <ArrowDownBold boxSize={6} color="brand.purple" />
            <Text color="brand.purple" textStyle="smallMedium">
              See More
            </Text>
          </HStack>
        </Box>
      )}
    </Box>
  );
}

export default TopUpHistoryTable;
